import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input as ChakraInput,
  Radio,
  RadioGroup,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Divider, Flex, Link, Spacer, Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { useHistory } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import { DefaultDiscountVIew } from '~/types/discount';
import {
  useExtensionDiscounts,
  useNotExtendingReasons,
} from '~/hooks/useExtensions/useExtensions';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import {
  ExtensionProposal,
  ProposalDiscounts,
} from '~/types/extensionProposal';
import { parse } from '~/services/Extensions';
import { formatCurrency } from '~/utils/formatCurrency';
import { ALL_FEATURES, ROUTER_PATHS } from '~/constants';
import { Datepicker, Input, ReactSelect, Textarea } from '~/components';
import { ContractEquipment, SelectItem } from '~/types';
import { ReasonForm } from './ReasonForm';
import { FileUploader } from '~/components/FileInput';
import { useExternoCan, useRoleCan } from '~/hooks';
import { EquipmentSuggestion } from '~/pages/Portal/Contract/EquipmentSuggestion';
import { ContractEquipamentValues } from '~/pages/Portal/Contract/ContractEquipamentsValues';

interface ExtensionRequestFormProps {
  id_contrato: Number;
  valor_mensal_contrato: Number;
  needsExtensionFile: boolean;
  extensionProposal?: ExtensionProposal;
  visualization: boolean;
  defaultCollectDate: Date;
  minCollectDate: Date;
  expirationDate: Date;
  showButtonToSeeAnalyse: boolean;
  termNumberMeetsStandard: boolean;
  termNumber?: string;
}

export const ExtensionRequestForm: React.FC<ExtensionRequestFormProps> = ({
  id_contrato,
  valor_mensal_contrato,
  needsExtensionFile,
  extensionProposal,
  visualization,
  defaultCollectDate,
  minCollectDate,
  showButtonToSeeAnalyse,
  expirationDate,
  termNumberMeetsStandard,
  termNumber,
}: ExtensionRequestFormProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const history = useHistory();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [elegivel, setElegivel] = useState('');
  const internoCan = useRoleCan({ roles: ['administrador', 'operacional'] });
  const externoCan = useExternoCan({ roles: ['cliente'] });
  const [manutencao, setManutencao] = useState('1');
  const [renovacao, setRenovacao] = useState(true);
  const [checkedItems, setCheckedItems] = useState<DefaultDiscountVIew[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [podeAnexarTermo, setPodeAnexarTermo] = useState(needsExtensionFile);
  const {
    data,
    isLoading,
    isFetching: discountIsFetching,
    refetch,
    isError,
  } = useExtensionDiscounts(id_contrato.toString());

  const {
    data: reasonsData,
    isLoading: reasonsIsLoading,
    isFetching: reasonsIsFetching,
    refetch: reasosnRefetch,
    isError: reasonsIsError,
  } = useNotExtendingReasons();

  const [isFetching, setFetching] = useState(false);

  // editar essa lista e a checkedItems em memoria?
  const [discountList, setDiscountList] = useState<DefaultDiscountVIew[]>([]);

  const [nomeArquivo, setNomeArquivo] = useState<string | undefined>('');
  const [arquivo, setSelectedFile] = useState<any>();
  const [motivo, setMotivo] = useState<string>('');
  const [nuTermo, setNuTermo] = useState<string>('');
  const [dataColeta, setDataColeta] = useState<Date>(defaultCollectDate);
  const [reasons, setReasons] = useState<SelectItem[]>([]);

  const [servicoInspecao, setServicoInspecao] = useState(false);
  const [servicoDesinstalacao, setServicoDesinstalacao] = useState(false);
  const [servicoEmbalagem, setServicoEmbalagem] = useState(false);

  const [equipamentos, setEquipamentos] = useState<ContractEquipment[]>([]);
  const [isLoadingEquipamentos, setIsLoadingEquipamentos] = useState(false);

  const parseDate = (date: string) => {
    const d = date?.split('-');
    return new Date(
      parseInt(d[0], 10),
      parseInt(d[1], 10) - 1,
      parseInt(d[2], 10),
    );
  };

  const handleFileChange = (_file: File | null) => {
    setSelectedFile(_file);
    setNomeArquivo(_file ? _file.name : '');
    clearErrors('arquivo');
  };

  const clearFile = () => {
    setSelectedFile(null);
    setNomeArquivo('');
    setValue('arquivo', null);
  };

  const handleOpen = () => {
    config();
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    config();
    onClose();
  };

  const clearValues = () => {
    setDiscountList(data || []);
    setCheckedItems([]);
    setManutencao('1');
    setRenovacao(true);
    setElegivel('');
    setServicoDesinstalacao(false);
    setServicoEmbalagem(false);
    setServicoInspecao(false);
    setDataColeta(defaultCollectDate);
    setMotivo('');
    setNuTermo(termNumber || '');
    setReasons([]);
    clearFile();
    setPodeAnexarTermo(needsExtensionFile);
    setEquipamentos([]);
    clearErrors();
  };

  useEffect(() => {
    if (!extensionProposal) {
      setDiscountList(data || []);
    }
  }, [data]);

  const config = () => {
    if (extensionProposal) {
      setElegivel(extensionProposal.elegivel ? '1' : '2');
      if (extensionProposal.elegivel) {
        carregarEquipamentos();
      }
      if (extensionProposal.elegivel) {
        const discounts = extensionProposal.descontos.map(
          (discount: ProposalDiscounts) => {
            return {
              id: uuid(),
              tempo_extensao: discount.prazo,
              desconto_padrao: discount.desconto,
              formattedDescontoPadrao: formatCurrency(
                parse(String(discount.desconto)),
              ).replace('R$', ''),
            } as unknown as DefaultDiscountVIew;
          },
        );
        setCheckedItems(discounts);
        setDiscountList(discounts);
        setNomeArquivo(extensionProposal.anexo_termo_extensao);
        setRenovacao(extensionProposal.renovacao_automatica);
        setManutencao(extensionProposal.manutencao_preventiva ? '1' : '2');
      } else {
        setMotivo(extensionProposal.motivo_cliente);
        setServicoDesinstalacao(extensionProposal.servico_desinstalacao);
        setServicoEmbalagem(extensionProposal.servico_embalagem);
        setServicoInspecao(extensionProposal.servico_inspecao);
        setDataColeta(
          parseDate(extensionProposal.data_limite_coleta_equipamento),
        );
        setReasons(
          extensionProposal.motivos.map(m => {
            return {
              label: m.motivo,
              value: m.id.toString(),
            };
          }),
        );
      }
    }
  };

  const validate = () => {
    clearErrors();
    elegivel === '1' &&
      checkedItems.forEach(desconto => {
        if (
          Number(desconto.desconto_padrao) < Number(desconto.desconto_minimo) ||
          Number(desconto.desconto_padrao) > Number(desconto.desconto_maximo)
        ) {
          setError(`descontos${desconto.id}`, {
            type: 'validate',
            message: `Desconto deve ser entre ${Number(desconto.desconto_minimo)
              .toString()
              .replace('.', ',')} e ${Number(desconto.desconto_maximo)
              .toString()
              .replace('.', ',')} %`,
          });

          !desconto.desconto_padrao &&
            setError(`descontos${desconto.id}`, {
              type: 'required',
              message: 'Campo obrigatório',
            });
        }
      });
    !visualization &&
      podeAnexarTermo &&
      elegivel === '1' &&
      !(arquivo || nomeArquivo) &&
      setError('arquivo', {
        type: 'required',
        message: 'Arquivo é obrigatório',
      });
    elegivel === '1' &&
      checkedItems.length === 0 &&
      setError('descontos', {
        type: 'required',
        message: 'Selecione ao menos um desconto',
      });

    elegivel === '2' &&
      reasons.length === 0 &&
      setError('motivos', {
        type: 'required',
        message: 'Campo obrigatório',
      });

    elegivel === '2' &&
      !dataColeta &&
      setError('dataColeta', {
        type: 'required',
        message: 'Campo obrigatório',
      });

    elegivel === '2' &&
      motivo.trim().length === 0 &&
      setError('motivo', {
        type: 'required',
        message: 'Campo obrigatório',
      });

    elegivel === '2' &&
      dataColeta < minCollectDate &&
      setError('dataColeta', {
        type: 'min',
        message: `Data da coleta não pode ser menor que ${minCollectDate.toLocaleDateString(
          'br',
        )}`,
      });

    elegivel === '2' &&
      motivo.length > 255 &&
      setError('motivo', {
        type: 'maxLength',
        message: 'Motivo com mais de 255 caracteres',
      });
    !termNumberMeetsStandard &&
      nuTermo.trim().length === 0 &&
      setError('nu_termo', {
        type: 'required',
        message: 'Campo obrigatório',
      });
    const { _formState } = control;
    return _formState.isValid;
  };

  const handleCreate = async () => {
    if (!validate()) {
      return;
    }
    setFetching(true);
    const updateData = {
      renovacao_automatica: renovacao,
      equipamentos: JSON.stringify(equipamentos),
    };
    const form = new FormData();
    form.append('contrato', id_contrato.toString());

    form.append('elegivel', elegivel === '1' ? 'true' : 'false');
    !termNumberMeetsStandard && form.append('nu_termo', nuTermo);
    if (elegivel === '1') {
      discountList.forEach(desconto => {
        if (checkedItems.some(item => item.id === desconto.id)) {
          form.append(
            'descontos',
            JSON.stringify({
              desconto: desconto.desconto_padrao.toString(),
              prazo: desconto.tempo_extensao.toString(),
              id_desconto: desconto.id,
            }),
          );
        }
      });
      form.append(
        'manutencao_preventiva',
        manutencao === '1' ? 'true' : 'false',
      );
      form.append('renovacao_automatica', renovacao ? 'true' : 'false');
      if (arquivo && podeAnexarTermo) {
        form.append('anexo_termo_extensao', arquivo);
      }
    } else {
      form.append('servico_inspecao', servicoInspecao ? 'true' : 'false');
      form.append(
        'data_limite_coleta_equipamento',
        dataColeta.toISOString().substring(0, 10),
      );
      form.append('motivo_cliente', motivo);
      form.append(
        'servico_desinstalacao',
        servicoDesinstalacao ? 'true' : 'false',
      );
      form.append('servico_embalagem', servicoEmbalagem ? 'true' : 'false');

      reasons.forEach(reason => {
        form.append(
          'motivos',
          JSON.stringify({
            id_motivo: reason.value,
          }),
        );
      });
    }
    equipamentos.forEach(eq => {
      form.append('equipamentos', JSON.stringify(eq));
    });

    const request = !visualization
      ? api.post(`proposta_extensao/`, form)
      : api.put(`proposta_extensao/${extensionProposal?.id}`, updateData);

    request
      .then(() => {
        toast({
          title: !visualization ? 'Proposta adicionada' : 'Proposta atualizada',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries(['contract', id_contrato]);
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            toast({
              title: `Erro ao ${
                !visualization ? 'criar' : 'atualizar'
              } proposta`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao ${!visualization ? 'criar' : 'atualizar'} proposta`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  };

  useEffect(() => {
    // verificar se o id existe na lista e setar o novo valor
    const novo = reasonsData?.filter(it => {
      return reasons.find(item => item.value === it.value);
    });
    setReasons(novo || []);
  }, [reasonsData]);

  const handleDesconto = (
    e: React.FormEvent<HTMLInputElement>,
    desconto: DefaultDiscountVIew,
  ) => {
    const value = parse(e.currentTarget.value);
    let index = checkedItems.findIndex(item => item.id === desconto.id);

    if (index > -1) {
      setCheckedItems([
        ...checkedItems.slice(0, index),
        {
          ...checkedItems[index],
          desconto_padrao: value,
          formattedDescontoPadrao: formatCurrency(value).replace('R$', ''),
        } as unknown as DefaultDiscountVIew,
        ...checkedItems.slice(index + 1),
      ]);
    }

    index = discountList.findIndex(item => item.id === desconto.id);

    setDiscountList([
      ...discountList.slice(0, index),
      {
        ...discountList[index],
        desconto_padrao: value,
        formattedDescontoPadrao: formatCurrency(value).replace('R$', ''),
      } as unknown as DefaultDiscountVIew,
      ...discountList.slice(index + 1),
    ]);
    clearErrors(`descontos${desconto.id}`);
  };

  const handleElegivel = (e: string) => {
    clearValues();
    setElegivel(e);
    clearErrors();
    if (e === '1' && !equipamentos.length) {
      carregarEquipamentos();
    }
  };

  const existsInCheckedItems = (desconto: DefaultDiscountVIew) => {
    return checkedItems.some(item => item.id === desconto.id);
  };

  const handleCheckbox = (desconto: DefaultDiscountVIew) => {
    const index = checkedItems.findIndex(item => item.id === desconto.id);
    if (index > -1) {
      // desconto removido
      setCheckedItems([
        ...checkedItems.slice(0, index),
        ...checkedItems.slice(index + 1),
      ]);
      clearErrors(`descontos${desconto.id}`);
    } else {
      // desconto adicionado
      setCheckedItems([...checkedItems, desconto]);
      clearErrors('descontos');
    }
  };

  const handleMotivo = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setMotivo(e.currentTarget.value);
    clearErrors('motivo');
  };

  const handleNuTermo = (e: React.FormEvent<HTMLInputElement>) => {
    setNuTermo(e.currentTarget.value);
    clearErrors('nu_termo');
  };

  const handleDataColeta = (date: Date) => {
    clearErrors('dataColeta');
    setDataColeta(date);
  };

  const handleRenovacao = () => {
    setRenovacao(!renovacao);
  };

  const handleReasonsChanges = (items: SelectItem[]) => {
    setReasons(items);
    clearErrors('motivos');
  };

  const verificarPodeAnexarTermo = async () => {
    if (nuTermo.length === 0) {
      return;
    }
    const response = api.get<{ precisa_anexo: boolean }>(
      `contratos/${id_contrato}/nu-termo/${nuTermo}`,
    );
    response
      .then(res => {
        setPodeAnexarTermo(res.data.precisa_anexo);
      })
      .catch(err => {
        toast({
          title: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const carregarEquipamentos = () => {
    if (!visualization) {
      setIsLoadingEquipamentos(true);
      const request = api.get<ContractEquipment[]>(
        `/proposta_extensao/equipamentos_valores?contrato_id=${id_contrato}`,
      );
      request
        .then(res => {
          const dataFormatted = res.data.map(item => ({
            ...item,
            valor_mensal: Number(Number(item.valor_mensal).toFixed(2)),
          }));
          setEquipamentos(dataFormatted);
        })
        .catch(e => {
          toast({
            title: `${e.response.data.detail}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        })
        .finally(() => setIsLoadingEquipamentos(false));
    } else {
      const equipaments: ContractEquipment[] = [];
      extensionProposal?.equipamentos.forEach(item => {
        equipaments.push({
          cd_equipamento: item.cd_equipamento,
          ds_equipamento: item.ds_equipamento,
          tipo_equipamento: item.tipo_equipamento,
          id: item.id,
          quantidade: item.quantidade,
          quantidade_original: item.quantidade_original,
          valor_mensal: Number(Number(item.valor_mensal).toFixed(2)),
          valor_mensal_unitario: item.valor_mensal_unitario,
        });
      });
      setEquipamentos(equipaments || []);
    }
  };

  const podeExtenderElegivel = elegivel === '1' && equipamentos.length > 0;

  return (
    <>
      {ALL_FEATURES && internoCan && showButtonToSeeAnalyse && (
        <Button
          color="white"
          background="gray.900"
          _hover={{
            bgColor: 'black',
          }}
          onClick={handleOpen}
        >
          {!visualization ? 'Analisar extensão' : 'Ver proposta de extensão'}
        </Button>
      )}

      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="xl"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader ml="1">Analisar extensão de contrato</DrawerHeader>
          <DrawerBody mx="1">
            <Stack gridGap={6} bg="white" w={800}>
              <FormControl isRequired>
                <FormLabel fontSize="20px">
                  O contrato é elegível para extensão?
                </FormLabel>
                <RadioGroup
                  value={elegivel}
                  onChange={e => handleElegivel(e)}
                  colorScheme="b"
                  isDisabled={visualization}
                >
                  <Stack direction="row" spacing={10}>
                    <Radio value="1">Sim</Radio>
                    <Radio value="2">Não</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {elegivel === '1' &&
              !isLoadingEquipamentos &&
              podeExtenderElegivel ? (
                <>
                  <Stack gridGap={1}>
                    <ContractEquipamentValues
                      valor_mensal_contrato={valor_mensal_contrato}
                      visualizacao={visualization}
                      equipamentos={equipamentos}
                      changeListaEquipamentos={e => setEquipamentos(e)}
                    />

                    <FormControl isRequired isInvalid={errors.descontos}>
                      <FormLabel>Prazo máximo de extensão e desconto</FormLabel>
                      {discountList.length > 0 ? (
                        <>
                          <Stack>
                            {discountList.map(desconto => (
                              <FormControl
                                isInvalid={errors[`descontos${desconto.id}`]}
                                w={350}
                                key={desconto.id}
                              >
                                <Flex height="55px">
                                  <Checkbox
                                    colorScheme="b"
                                    value={desconto.id}
                                    onChange={() => handleCheckbox(desconto)}
                                    isChecked={existsInCheckedItems(desconto)}
                                    isDisabled={visualization}
                                  >
                                    <>
                                      {desconto.tempo_extensao}
                                      {desconto.tempo_extensao > 1
                                        ? ' meses'
                                        : ' mês'}
                                      {!visualization && (
                                        <Text
                                          fontSize="10px"
                                          marginBottom="-15px"
                                        >
                                          Desconto permitido:{' '}
                                          {Number(desconto.desconto_minimo)
                                            .toString()
                                            .replace('.', ',')}{' '}
                                          a{' '}
                                          {Number(desconto.desconto_maximo)
                                            .toString()
                                            .replace('.', ',')}{' '}
                                          %
                                        </Text>
                                      )}
                                    </>
                                  </Checkbox>
                                  <Spacer />
                                  <Stack
                                    direction="row"
                                    w="40%"
                                    alignItems="center"
                                  >
                                    <ChakraInput
                                      marginBottom="-15px"
                                      bg="gray.100"
                                      borderColor={
                                        errors[`descontos${desconto.id}`]
                                          ? 'red'
                                          : 'gray.300'
                                      }
                                      _hover={{
                                        borderColor: 'gray.500',
                                      }}
                                      _placeholder={{
                                        color: 'gray.500',
                                      }}
                                      value={desconto.formattedDescontoPadrao}
                                      onChange={e =>
                                        handleDesconto(e, desconto)
                                      }
                                      isDisabled={visualization}
                                    />
                                    <Text pt="15px">%</Text>
                                  </Stack>
                                </Flex>
                                {errors[`descontos${desconto.id}`] && (
                                  <FormErrorMessage
                                    justifyContent="start"
                                    marginBottom="-15px"
                                  >
                                    {errors[`descontos${desconto.id}`]?.message}
                                  </FormErrorMessage>
                                )}
                              </FormControl>
                            ))}
                          </Stack>
                          {!!errors.descontos && (
                            <FormErrorMessage>
                              {errors.descontos?.message}
                            </FormErrorMessage>
                          )}
                          <Alert
                            bg="#f7f2fa"
                            borderRadius="md"
                            color="#9246ce"
                            fontSize="sm"
                            py={1}
                            px={2}
                            mt={4}
                            mb={2}
                          >
                            <AlertTitle>Atenção:</AlertTitle>
                            <AlertDescription>
                              Esse desconto será adicionado ao desconto de
                              aceite através do portal.
                            </AlertDescription>
                          </Alert>
                        </>
                      ) : (
                        <Text>
                          Não há desconto padrão cadastrado!{' '}
                          <Link
                            color="gray.800"
                            sx={{ textDecor: 'underline', fontWeight: 'bold' }}
                            href={ROUTER_PATHS.PORTAL.ADMIN.DISCOUNT}
                          >
                            Criar desconto?
                          </Link>
                        </Text>
                      )}
                    </FormControl>
                    {!termNumberMeetsStandard && (
                      <FormControl isInvalid={errors.nu_termo}>
                        <FormLabel
                          justifyContent="space-between"
                          display="flex"
                        >
                          <HStack>
                            <Text>Informe o número do termo do Contrato</Text>
                            <Text color="red">*</Text>
                          </HStack>
                          <Tooltip
                            hasArrow
                            label={
                              'Esse número precisa ser inserido no formato: "CNPJ - sequência de dígitos". Ex:12345678000123-000'
                            }
                            bg="gray"
                            shouldWrapChildren
                          >
                            <Icon as={FaInfoCircle} color="gray.900" />
                          </Tooltip>
                        </FormLabel>
                        <Input
                          name="nu_termo"
                          error={errors?.nu_termo}
                          value={nuTermo}
                          onChange={e => handleNuTermo(e)}
                          isDisabled={visualization}
                          onBlur={() => verificarPodeAnexarTermo()}
                        />
                      </FormControl>
                    )}
                  </Stack>
                  <FormControl isRequired>
                    <FormLabel fontSize="20px">
                      Disponibilizar manutenção preventiva?
                    </FormLabel>
                    <RadioGroup
                      value={manutencao}
                      onChange={e => setManutencao(e)}
                      colorScheme="b"
                      isDisabled={visualization}
                    >
                      <Stack direction="row" spacing={10}>
                        <Radio value="1">Sim</Radio>
                        <Radio value="2">Não</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                  {podeAnexarTermo && (
                    <FileUploader
                      isDisabled={visualization || isFetching}
                      fileName={nomeArquivo}
                      onChange={handleFileChange}
                      clearFile={clearFile}
                      label="Anexar termo de extensão"
                      errors={errors.arquivo}
                      helpText="Informar o termo de extensão em formato PDF, máximo 5MB."
                    />
                  )}
                  <FormControl>
                    <FormLabel fontSize="20px">Renovação automática</FormLabel>
                    <Checkbox
                      colorScheme="b"
                      isChecked={renovacao}
                      onChange={handleRenovacao}
                    >
                      Permitir renovação automática caso o cliente não responda
                      em até 90 dias.
                    </Checkbox>
                  </FormControl>
                </>
              ) : elegivel === '1' &&
                !isLoadingEquipamentos &&
                !podeExtenderElegivel ? (
                <Text>Não há equipamentos para o contrato.</Text>
              ) : elegivel === '1' && isLoadingEquipamentos ? (
                <Stack
                  gridGap={3}
                  textAlign="center"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Spinner />
                  <Text>Carregando equipamentos...</Text>
                </Stack>
              ) : elegivel === '2' ? (
                <>
                  {id_contrato && (
                    <Accordion allowToggle>
                      <AccordionItem>
                        <AccordionButton bgColor="#F2F2F2">
                          <AccordionIcon mr={3} />
                          <Box
                            as="span"
                            flex="1"
                            textAlign="left"
                            fontSize={13}
                            fontWeight={500}
                          >
                            Visualizar sugestões de novos equipamentos
                          </Box>
                        </AccordionButton>

                        <AccordionPanel pb={4} bgColor="#F2F2F2">
                          <EquipmentSuggestion id_contrato={id_contrato} />
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  )}
                  <Stack gridGap={1}>
                    <Flex>
                      <ReactSelect
                        isMulti
                        isSearchable
                        label="Motivo do contrato não ser estendido"
                        placeholder="Selecione"
                        error={errors.motivos}
                        options={reasonsData}
                        value={reasons}
                        maxW="90%"
                        required
                        isDisabled={visualization}
                        onChange={changes =>
                          handleReasonsChanges(changes as SelectItem[])
                        }
                      />
                      {!visualization && (
                        <Flex h="auto">
                          <ReasonForm align={!!errors?.motivos} />
                        </Flex>
                      )}
                    </Flex>
                    <FormControl isInvalid={errors.motivo} isRequired>
                      <FormLabel fontSize="14px">
                        Compartilhe com o cliente o motivo do contrato não ser
                        estendido
                      </FormLabel>
                      <Textarea
                        name="descricao"
                        resize="none"
                        error={errors?.motivo}
                        value={motivo}
                        onChange={e => handleMotivo(e)}
                        fontSize="15px"
                        isDisabled={visualization}
                      />
                    </FormControl>
                    <FormControl
                      isInvalid={errors.dataColeta}
                      isRequired
                      w="50%"
                    >
                      <FormLabel fontSize="14px">
                        Selecione a data limite para a coleta
                      </FormLabel>
                      <Datepicker
                        isDisabled={visualization}
                        onChange={e => handleDataColeta(e)}
                        selectedDate={dataColeta}
                        error={errors.dataColeta}
                        // filterDate={date => date >= minCollectDate}
                      />
                    </FormControl>
                  </Stack>
                  <FormControl>
                    <FormLabel fontSize="20px">
                      Selecione os serviços adicionais necessários:
                    </FormLabel>
                    <Stack>
                      <Checkbox
                        colorScheme="b"
                        isChecked={servicoInspecao}
                        onChange={() => {
                          setServicoInspecao(!servicoInspecao);
                        }}
                        isDisabled={visualization}
                      >
                        Inspeção
                      </Checkbox>
                      <Checkbox
                        colorScheme="b"
                        isChecked={servicoDesinstalacao}
                        onChange={() => {
                          setServicoDesinstalacao(!servicoDesinstalacao);
                        }}
                        isDisabled={visualization}
                      >
                        Desinstalação
                      </Checkbox>
                      <Checkbox
                        colorScheme="b"
                        isChecked={servicoEmbalagem}
                        onChange={() => {
                          setServicoEmbalagem(!servicoEmbalagem);
                        }}
                        isDisabled={visualization}
                      >
                        Embalagem
                      </Checkbox>
                    </Stack>
                  </FormControl>
                </>
              ) : null}
              {visualization && (
                <Alert
                  bg="#f7f2fa"
                  borderRadius="md"
                  color="#9246ce"
                  fontSize="sm"
                  py={1}
                  px={2}
                  my={2}
                >
                  <AlertTitle>Atenção:</AlertTitle>
                  <AlertDescription>
                    E-mail enviado ao cliente. Aguardando resposta.
                  </AlertDescription>
                </Alert>
              )}
              <Divider orientation="horizontal" />
              {!visualization && !!elegivel && (
                <Text size="sm">
                  Ao confirmar, avisaremos ao cliente que o Contrato{' '}
                  {elegivel === '2' ? 'não' : ''} está elegível para extensão
                  {elegivel === '2'
                    ? '. E antes do Contrato vencer, enviaremos as instruções para a coleta dos equipamentos.'
                    : ' conforme os prazos que você selecionou.'}
                </Text>
              )}
              {!!elegivel && (
                <Text fontSize="sm" color="gray.500">
                  <span style={{ fontWeight: 'bold', color: 'red' }}>*</span>{' '}
                  Campos obrigatórios
                </Text>
              )}
            </Stack>
            <Flex justifyContent="end" mt={2}>
              <Button
                type="submit"
                onClick={handleSubmit(handleCreate)}
                color="white"
                background="gray.900"
                size="lg"
                _hover={{
                  bgColor: 'black',
                }}
                isDisabled={
                  isFetching ||
                  isLoadingEquipamentos ||
                  (elegivel === '1' && equipamentos.length === 0)
                }
                display={elegivel === '2' && visualization ? 'none' : ''}
              >
                {visualization ? 'Salvar' : 'Confirmar'}{' '}
                {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
              </Button>
              <Button
                type="button"
                onClick={handleClose}
                color="black"
                size="lg"
                background="transparent"
                ml="10px"
                isDisabled={isFetching}
              >
                Cancelar
              </Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
